import { useState, useEffect, useRef } from 'react';

/**
 * Searchable single-select dropdown. Click to open, type to filter options.
 * Closes on outside click. Pass an empty-value option (e.g. "All Shows") to
 * allow clearing the selection.
 */
export default function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Search...',
  disabled = false,
}: {
  value: string;
  onChange: (val: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedLabel = options.find((o) => o.value === value)?.label || '';

  const filtered = options
    .filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.label.localeCompare(b.label));

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
            setSearch('');
            setTimeout(() => inputRef.current?.focus(), 0);
          }
        }}
        className={`w-full px-4 py-2.5 border border-warm-200 rounded-lg text-left flex items-center justify-between transition-colors ${
          disabled
            ? 'bg-warm-50 text-warm-400 cursor-not-allowed'
            : 'bg-white hover:border-warm-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent'
        }`}
      >
        <span className={value ? 'text-warm-900' : 'text-warm-400'}>
          {selectedLabel || placeholder}
        </span>
        <svg className="w-4 h-4 text-warm-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d={isOpen ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-warm-200 rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-warm-100">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Type to search..."
              className="w-full px-3 py-2 text-sm border border-warm-200 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <ul className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-warm-400">No results found</li>
            ) : (
              filtered.map((opt) => (
                <li
                  key={opt.value}
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                    opt.value === value
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-warm-700 hover:bg-warm-50'
                  }`}
                >
                  {opt.label}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
